import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Badge,
    Button,
    Card,
    Form,
    Pagination,
    Spinner,
    Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../../../layouts/PageLayout";

const apiUrl = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser") || ""}`,
    Accept: "application/json",
});

const formatValue = (value) => (
    value === null || value === undefined || value === "" ? "—" : value
);

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const getErrorMessage = (error, fallback) => {
    const errors = error.response?.data?.errors;
    if (errors && typeof errors === "object") {
        const messages = Object.values(errors).flat().filter(Boolean);
        if (messages.length) return messages.join(" ");
    }
    return error.response?.data?.message || error.message || fallback;
};

const getListingData = (response, requestedPage, requestedPageSize) => {
    const root = response?.data ?? {};
    const data = root.data ?? root;
    const shipments = Array.isArray(data)
        ? data
        : Array.isArray(data.shipments)
            ? data.shipments
            : Array.isArray(data.data)
                ? data.data
                : Array.isArray(root.shipments)
                    ? root.shipments
                    : [];
    const stores = Array.isArray(root.stores)
        ? root.stores
        : Array.isArray(data.stores)
            ? data.stores
            : [];
    const meta = root.meta
        || data.meta
        || root.pagination
        || data.pagination
        || (data.current_page !== undefined ? data : null);
    const currentPage = Number(meta?.current_page) || requestedPage;
    const perPage = Number(meta?.per_page) || requestedPageSize;
    const total = Number(meta?.total);
    const lastPage = Number(meta?.last_page)
        || (Number.isFinite(total) && perPage ? Math.max(1, Math.ceil(total / perPage)) : 1);

    return {
        shipments,
        stores,
        pagination: {
            currentPage,
            lastPage,
            perPage,
            total: Number.isFinite(total) ? total : shipments.length,
            from: Number(meta?.from) || (shipments.length ? (currentPage - 1) * perPage + 1 : 0),
            to: Number(meta?.to) || (shipments.length ? (currentPage - 1) * perPage + shipments.length : 0),
        },
    };
};

export default function SellerShippedProductsPage() {
    const navigate = useNavigate();
    const [shipments, setShipments] = useState([]);
    const [stores, setStores] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        perPage: 10,
        total: 0,
        from: 0,
        to: 0,
    });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [storeId, setStoreId] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadProducts = useCallback(async (signal) => {
        setLoading(true);
        setError("");
        try {
            const params = {
                page,
                per_page: pageSize,
                ...(search.trim() ? { search: search.trim() } : {}),
                ...(storeId ? { store_id: storeId } : {}),
                ...(statusFilter ? { status: statusFilter } : {}),
            };
            const response = await axios.get(`${apiUrl}/admin/seller-shipped-products`, {
                headers: authHeaders(),
                params,
                signal,
            });
            const result = getListingData(response, page, pageSize);
            setShipments(result.shipments);
            setStores(result.stores);
            setPagination(result.pagination);
        } catch (requestError) {
            if (requestError.code !== "ERR_CANCELED" && requestError.name !== "CanceledError") {
                setError(getErrorMessage(requestError, "Could not load seller shipments."));
                setShipments([]);
            }
        } finally {
            if (signal?.aborted !== true) setLoading(false);
        }
    }, [page, pageSize, search, storeId, statusFilter]);

    useEffect(() => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => loadProducts(controller.signal), 250);
        return () => {
            window.clearTimeout(timeout);
            controller.abort();
        };
    }, [loadProducts]);

    const pageNumbers = [];
    const firstPage = Math.max(1, pagination.currentPage - 2);
    const lastPage = Math.min(pagination.lastPage, pagination.currentPage + 2);
    for (let number = firstPage; number <= lastPage; number += 1) pageNumbers.push(number);

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h3 className="mc-breadcrumb-title">Seller Shipments</h3>
                        <p className="text-muted mb-0">Review each seller shipment and its products, then confirm warehouse receipt.</p>
                    </div>
                    <Button variant="primary" onClick={() => loadProducts()}>
                        Refresh
                    </Button>
                </div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError("")}>
                    {error}
                    <Button variant="link" className="p-0 ms-2 align-baseline" onClick={() => loadProducts()}>
                        Retry
                    </Button>
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <Form.Label htmlFor="shipped-product-search">Search shipments or products</Form.Label>
                            <Form.Control
                                id="shipped-product-search"
                                type="search"
                                placeholder="Shipment ID, tracking number, title or product code"
                                value={search}
                                onChange={(event) => {
                                    setSearch(event.target.value);
                                    setPage(1);
                                }}
                            />
                        </div>
                        <div className="col-md-3">
                            <Form.Label htmlFor="shipped-product-status">Receipt status</Form.Label>
                            <Form.Select
                                id="shipped-product-status"
                                value={statusFilter}
                                onChange={(event) => {
                                    setStatusFilter(event.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All statuses</option>
                                <option value="pending">Pending Receive</option>
                                <option value="received">Received</option>
                            </Form.Select>
                        </div>
                        <div className="col-md-3">
                            <Form.Label htmlFor="shipped-product-store">Store</Form.Label>
                            <Form.Select
                                id="shipped-product-store"
                                value={storeId}
                                onChange={(event) => {
                                    setStoreId(event.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All stores</option>
                                {stores.map((store) => (
                                    <option key={store.id} value={store.id}>{store.name || `Store #${store.id}`}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="col-md-2">
                            <Form.Label htmlFor="shipped-product-page-size">Rows</Form.Label>
                            <Form.Select
                                id="shipped-product-page-size"
                                value={pageSize}
                                onChange={(event) => {
                                    setPageSize(Number(event.target.value));
                                    setPage(1);
                                }}
                            >
                                {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                            </Form.Select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" role="status" /></div>
                    ) : shipments.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            {error ? "Shipments could not be loaded." : "No shipments found matching your filters."}
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>Shipment</th>
                                            <th>Courier & Store</th>
                                            <th>Products & Codes</th>
                                            <th>Dispatched</th>
                                            <th>Receipt Status</th>
                                            <th className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {shipments.map((shipment) => {
                                            const isReceived = shipment.status_2 !== null && shipment.status_2 !== undefined && shipment.status_2 !== "";
                                            return (
                                                <tr key={shipment.id}>
                                                    <td>
                                                        <div className="fw-semibold text-dark">Shipment #{shipment.id}</div>
                                                        <small className="text-muted">Tracking: {formatValue(shipment.tracking_number)}</small>
                                                    </td>
                                                    <td>
                                                        <div>{formatValue(shipment.courier_company?.company_name)}</div>
                                                        <small className="text-muted">{formatValue(shipment.store?.name)}</small>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {(shipment.products || []).map((product) => (
                                                                <Badge bg="light" text="dark" className="border" key={product.shipment_item_id || product.id}>
                                                                    {formatValue(product.product_code)}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                        <small className="text-muted">
                                                            {shipment.product_count ?? shipment.products?.length ?? 0} product(s)
                                                        </small>
                                                    </td>
                                                    <td>{formatDate(shipment.dispatched_at)}</td>
                                                    <td>
                                                        {isReceived ? (
                                                            <Badge bg="success">Received</Badge>
                                                        ) : (
                                                            <Badge bg="warning" text="dark">Pending Receive</Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <Button
                                                            size="sm"
                                                            variant="primary"
                                                            onClick={() => navigate(`/admin/seller-shipped-products/${shipment.id}`, { state: { shipment } })}
                                                        >
                                                            View shipment
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4">
                                <small className="text-muted">
                                    Showing {pagination.from}–{pagination.to} of {pagination.total} shipments
                                </small>
                                {pagination.lastPage > 1 && (
                                    <Pagination className="mb-0">
                                        <Pagination.First disabled={pagination.currentPage <= 1} onClick={() => setPage(1)} />
                                        <Pagination.Prev disabled={pagination.currentPage <= 1} onClick={() => setPage(pagination.currentPage - 1)} />
                                        {pageNumbers.map((number) => (
                                            <Pagination.Item
                                                key={number}
                                                active={number === pagination.currentPage}
                                                onClick={() => setPage(number)}
                                            >
                                                {number}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next
                                            disabled={pagination.currentPage >= pagination.lastPage}
                                            onClick={() => setPage(pagination.currentPage + 1)}
                                        />
                                        <Pagination.Last
                                            disabled={pagination.currentPage >= pagination.lastPage}
                                            onClick={() => setPage(pagination.lastPage)}
                                        />
                                    </Pagination>
                                )}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </PageLayout>
    );
}